// ===== WebSocket 페이로드 계약 가드 =====
// 와이어 페이로드 타입 + 런타임 형태 검증(isWs*/validator 테이블) + 핸들러 프록시.
// classic script로 로드되며 ws-handlers.js보다 먼저 실려야 한다(index.html 순서).

// Phase 4(bigtionax-chat-attachments): WsHistoryMessage/WsPayload 는 여기서 ChatAttachment(chat.ts
// 소유)를 참조한다 — classic script 전역 병합(§전역 스코프 공유)이라 로드 순서와 무관하게
// 타입체크가 가능하다.
// Phase 4(bigtionax-chat-item-transfer): item-transfer.ts(서버) TransferMeta의 클라 표시용
// 부분집합 — forwardedByName/fromRoomTitle은 자유 문자열(자기 프로필/룸 개명)이라 chat.ts 렌더 시
// escapeHtml 필수(chat.ts:142-146 이름·발췌 escapeHtml 선례).
type TransferCardMeta = { forwardedBy?: string; forwardedByName?: string; fromRoomId?: string; fromRoomTitle?: string; transferredAt?: string };
// Phase 3(bigtionax-memory-read-g-source-marker): memorySourceCount는 optional — guard는 이
// 필드를 검증하지 않는다(§설계). 잘못된 값이 섞여도 isWsHistoryMessage는 계속 통과시켜 history
// 전체가 거부되지 않게 한다. 렌더러(memory-source-marker.ts attach)가 정수 >=1이 아니면 무시한다.
type WsHistoryMessage = { time?: string | number; artifact?: unknown; role?: string; sender?: string; senderName?: string; senderAvatar?: string; content: string; thinking?: string; seq?: number; replyTo?: ReplyQuoteView; attachments?: ChatAttachment[]; transfer?: TransferCardMeta; memorySourceCount?: number };
type WsRemoteEvent = Record<string, unknown> & { invokerClientId?: unknown; invokerActorId?: unknown };
type WsPayload = Record<string, unknown> & {
  sessionId: string; roomId: string; invokerClientId: string; invokerActorId: string;
  content: string; delta: string; expression: string; message: string; status: string; stage: string; detail: string;
  agent: string; agentName: string; speaker: string; speakerName: string; typingStatus: string;
  sender: string; senderName: string; senderAvatar: string; text: string; ts: string | number;
  command: string; error: string; reason: string; featureId: string; routineName: string; duration: string;
  replace: boolean; isTyping: boolean; prepend: boolean; hasMore: boolean; busyStuck: boolean;
  fallbackToAgent: boolean; ok: boolean; inProgress: boolean; serverRestarted: boolean;
  hasActiveProcess: boolean; groupChatBusy: boolean;
  offset: number; count: number; missedMessageCount: number; missedCount: number; flushedCount: number;
  // Phase 16(bigtionax-chat-reply-reactions-read-receipts): message-unread-total 페이로드의
  // 개인 안 읽은 메시지 총계(앱 배지 소스).
  unreadCount: number;
  // P13(bigtionax-chat-mention-reply-reaction, D10): 와이어 최상위 메시지 앵커는 logSeq다 — 전송 계층
  // seq(LiveSession.send() 재접속 큐 카운터)와 이름이 충돌하지 않게 분리했다. seq는 여기서 쓰지 않는다.
  logSeq: number;
  // Phase 3(bigtionax-memory-read-g-source-marker): speaker-end 페이로드가 persisted(logSeq 확정)
  // 일 때만 싣는 전달 기억 개수 — optional이며 guard는 검증하지 않는다(WsHistoryMessage와 동일 계약).
  memorySourceCount?: number;
  transitionId: string;
  recentSessions: CoreSessionSummary[]; sessions: CoreSessionSummary[]; messages: WsHistoryMessage[];
  activeSpeakers: string[]; evictedClientIds: string[]; notes: unknown[]; misses: unknown[];
  artifact: unknown; data: Record<string, unknown>; thinking: string; returnSpeech: string;
  replyTo: ReplyQuoteView;
  clientMessageId: string;
  // Phase 4(bigtionax-chat-attachments): room-message 페이로드의 첨부 배열 — 부재/빈 배열이면
  // 서버가 키 자체를 싣지 않는다(logSeq 선례와 동일한 additive 계약, room-broadcast.ts createRoomMessage).
  attachments: ChatAttachment[];
  // Phase 4(bigtionax-chat-item-transfer): item-transferred 페이로드의 전달 메타(표시 전용).
  transfer: TransferCardMeta;
  job: Record<string, unknown>;
  jobId: string;
  sourceUrl: string;
  videoId: string;
  notePath: string;
  artifactId: string;
  warningLabels: string[];
  cancelRequestedAt: string;
};
function isWsRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }
// Phase 4(bigtionax-chat-attachments): attachments 비대칭 검증 — 신뢰 경계 밖 입력(온라인 room-message)은
// 서버 sanitizeIncomingAttachments(ws-handler.ts)와 정렬된 strict 원소 검증(§path allowlist)으로
// 위조 path/과다 배열을 fail-closed 전량 폐기한다. 반면 session-history 복원(isWsHistoryMessage)은
// 형만 관대 검증한다 — 과거 저장분이 미래 path 규칙 강화에 걸려 히스토리 전체가 every() 폐기로
// 사라지는 것을 방지한다(정상 트래픽은 이미 서버 sanitize 통과본이라 lenient 완화가 위험을 늘리지 않는다).
const WS_ATTACHMENT_PATH_RE = /^\/api\/files\/file%3A[A-Za-z0-9._-]+\/content$/i;
function isWsStrictAttachment(value: unknown): boolean {
  return isWsRecord(value) && typeof value.name === "string"
    && typeof value.path === "string" && WS_ATTACHMENT_PATH_RE.test(value.path);
}
function isWsStrictAttachments(value: unknown): boolean {
  return Array.isArray(value) && value.length <= 10 && value.every(isWsStrictAttachment);
}
function isWsLenientAttachment(value: unknown): boolean {
  return isWsRecord(value) && typeof value.name === "string" && typeof value.path === "string";
}
function isWsLenientAttachments(value: unknown): boolean {
  return Array.isArray(value) && value.every(isWsLenientAttachment);
}
function isWsStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}
function isWsSessionSummary(value: unknown): value is CoreSessionSummary {
  return isWsRecord(value)
    && typeof value.id === "string"
    && typeof value.startedAt === "string"
    && typeof value.title === "string"
    && typeof value.messageCount === "number"
    && Number.isFinite(value.messageCount)
    && value.messageCount >= 0
    && (value.owner === undefined || typeof value.owner === "string")
    && (value.ownerActorId === undefined || value.ownerActorId === null || typeof value.ownerActorId === "string")
    && (value.summary === undefined || value.summary === null || typeof value.summary === "string");
}
// P7(bigtionax-chat-mention-reply-reaction): optional 검사 — 필수로 만들면 replyTo 없는 구버전
// 페이로드가 전부 폐기된다.
function isWsOptionalReplyQuote(value: unknown): boolean {
  if (value === undefined) return true;
  return isWsRecord(value)
    && typeof value.seq === "number" && Number.isFinite(value.seq)
    && (value.senderName === null || typeof value.senderName === "string")
    && typeof value.excerpt === "string";
}
function isWsHistoryMessage(value: unknown): value is WsHistoryMessage {
  return isWsRecord(value)
    && typeof value.content === "string"
    && (value.time === undefined || typeof value.time === "string" || typeof value.time === "number")
    && (value.role === undefined || typeof value.role === "string")
    && (value.sender === undefined || typeof value.sender === "string")
    && (value.senderName === undefined || value.senderName === null || typeof value.senderName === "string")
    && (value.senderAvatar === undefined || value.senderAvatar === null || typeof value.senderAvatar === "string")
    && (value.thinking === undefined || typeof value.thinking === "string")
    && isWsOptionalFiniteNumber(value, "seq")
    && isWsOptionalReplyQuote(value.replyTo)
    && (value.attachments === undefined || isWsLenientAttachments(value.attachments));
}
function isWsSearchResult(value: unknown): value is { path: string; name: string } {
  return isWsRecord(value) && typeof value.path === "string" && typeof value.name === "string";
}
function isWsOptionalFiniteNumber(data: Record<string, unknown>, key: string): boolean {
  const value = data[key];
  return value === undefined || (typeof value === "number" && Number.isFinite(value));
}
function isWsOptionalBoolean(data: Record<string, unknown>, key: string): boolean {
  return data[key] === undefined || typeof data[key] === "boolean";
}
function isWsOptionalString(data: Record<string, unknown>, key: string): boolean {
  return data[key] === undefined || typeof data[key] === "string";
}
const wsRequiredStringFields: Record<string, readonly string[]> = {
  onStream: ["content"],
  onIdleWarning: ["message"],
  onSummaryStatus: ["status"],
  onDelegation: ["content"],
  onAgentStart: ["agent", "agentName"],
  onAgentStream: ["agent", "delta"],
  onAgentThinking: ["agent", "delta"],
  onAgentThinkingDone: ["agent"],
  onAgentComplete: ["agent", "agentName"],
  onSpeakerMemoryLoading: ["sessionId", "speaker", "speakerName"],
  onSpeakerStart: ["sessionId", "speaker", "speakerName"],
  onGroupChatStream: ["sessionId", "speaker", "delta"],
  onGroupChatThinking: ["sessionId", "speaker", "delta"],
  onGroupChatThinkingDone: ["speaker"],
  onSpeakerEnd: ["sessionId", "speaker", "speakerName"],
  onGroupChatDone: ["sessionId"],
  onRoutineReport: ["status", "routineName"],
  onHarnessAssumptionsReady: ["featureId"],
  onHarnessAssumptionsRedefine: ["featureId"],
  onHarnessRehearsalComplete: ["featureId"],
  // P10(bigtionax-chat-mention-reply-reaction, §설계A): guardWsHandlers는 레코드면 통과시키므로
  // 별도 validator 없이도 이 필수 문자열 필드 하나만 additive로 등록한다.
  onMessageReaction: ["sessionId"],
  onMessageReadUpdate: ["sessionId"],
  // Phase 2(bigtionax-chat-session-attribution-contract, §설계e): 클라가 전역 currentSessionId
  // 대신 payload sessionId로 방을 귀속하므로, sessionId 없는 session-history는 여기서 거부한다.
  onSessionHistory: ["sessionId"],
  onSessionTransition: ["transitionId", "sessionId", "status"],
  // Phase 15(bigtionax-chat-reply-reactions-read-receipts): 이름 외 raw actorId를 싣지 않으므로
  // senderName이 항상 필수 문자열이다(NEUTRAL_CALLER_NAME 폴백 포함 — 서버가 항상 채운다).
  onRoomTyping: ["sessionId", "senderName"],
  onYoutubeDigestJobUpdate: ["sessionId", "jobId"],
};
const wsOptionalStringFields: Record<string, readonly string[]> = {
  onStream: ["expression"],
  onComplete: ["content", "expression"],
  onError: ["message", "expression"],
  onStatus: ["status", "stage", "detail", "agent", "message"],
  onSessionTransition: ["reason"],
  onNoteCards: ["header", "expression"],
  onSummaryStatus: ["message"],
  onAgentStart: ["typingStatus"],
  onAgentStream: ["expression"],
  onAgentComplete: ["content", "expression", "thinking"],
  onAgentEnd: ["agent", "returnSpeech"],
  onGroupChatStream: ["expression"],
  onSpeakerEnd: ["content", "expression"],
  onGroupChatCatchup: ["content", "speaker"],
  onStreamCatchup: ["content"],
  onAgentStreamCatchup: ["content", "agent"],
  onRoutineReport: ["duration", "error"],
  onServerRestarting: ["reason"],
  onNotificationCanonical: ["sourceEventKey", "targetPath"],
  onYoutubeDigestJobUpdate: ["roomId", "sourceUrl", "videoId", "error", "notePath", "artifactId", "cancelRequestedAt"],
};
const wsOptionalBooleanFields: Record<string, readonly string[]> = {
  onStream: ["replace"],
  onGroupChatCatchup: ["inProgress"],
  onStreamCatchup: ["inProgress"],
  onAgentStreamCatchup: ["inProgress"],
};
type WsHandlerPayloadValidator = (data: Record<string, unknown>) => boolean;

function isValidFailedCommandResultPayload(data: Record<string, unknown>): boolean {
  return isWsOptionalString(data, "command")
    && isWsOptionalBoolean(data, "fallbackToAgent")
    && isWsOptionalString(data, "error")
    && (data.fallbackToAgent !== true || typeof data.command === "string");
}

function isValidSearchCommandData(commandData: unknown): boolean {
  if (!isWsRecord(commandData) || !Array.isArray(commandData.results)) return false;
  return commandData.results.every(isWsSearchResult)
    && isWsOptionalString(commandData, "query")
    && isWsOptionalFiniteNumber(commandData, "total");
}

function isValidSuccessfulCommandResultPayload(data: Record<string, unknown>): boolean {
  if (typeof data.command !== "string") return false;
  const commandData = data.data;
  if (commandData !== undefined && commandData !== null && !isWsRecord(commandData)) return false;
  if (["/search", "/s", "/find"].includes(data.command)) return isValidSearchCommandData(commandData);
  if (commandData === undefined || commandData === null) return true;
  return isWsRecord(commandData)
    && isWsOptionalString(commandData, "message")
    && isWsOptionalString(commandData, "html");
}

function isValidCommandResultPayload(data: Record<string, unknown>): boolean {
  if (typeof data.ok !== "boolean") return false;
  return data.ok
    ? isValidSuccessfulCommandResultPayload(data)
    : isValidFailedCommandResultPayload(data);
}

const wsHandlerPayloadValidators: Record<string, WsHandlerPayloadValidator> = {
  onTyping: (data) => typeof data.isTyping === "boolean",
  onInboxCount: (data) => typeof data.count === "number" && Number.isFinite(data.count) && data.count >= 0,
  onSessionInfo: (data) => (data.sessionId === null || typeof data.sessionId === "string")
    && Array.isArray(data.recentSessions) && data.recentSessions.every(isWsSessionSummary)
    && typeof data.hasMore === "boolean"
    && (data.roomId === undefined || data.roomId === null || typeof data.roomId === "string")
    && isWsOptionalBoolean(data, "busyStuck"),
  onSessionTransition: (data) => data.status === "committed" || data.status === "denied",
  onMoreSessions: (data) => Array.isArray(data.sessions) && data.sessions.every(isWsSessionSummary)
    && typeof data.hasMore === "boolean",
  onSearchSessionsResult: (data) => Array.isArray(data.sessions) && data.sessions.every(isWsSessionSummary),
  onSessionHistory: (data) => Array.isArray(data.messages) && data.messages.every(isWsHistoryMessage)
    && typeof data.hasMore === "boolean"
    && isWsOptionalFiniteNumber(data, "offset")
    && isWsOptionalBoolean(data, "prepend"),
  onReconnectState: (data) => (data.activeSpeakers === undefined || isWsStringArray(data.activeSpeakers))
    && isWsOptionalFiniteNumber(data, "missedMessageCount")
    && (data.sessionId === undefined || data.sessionId === null || typeof data.sessionId === "string")
    && ["hasActiveProcess", "groupChatBusy", "serverRestarted"].every((key) => isWsOptionalBoolean(data, key)),
  onSessionDeleted: (data) => typeof data.sessionId === "string" && isWsStringArray(data.evictedClientIds),
  onSpeakerEnd: (data) => data.thinking === undefined || data.thinking === null || typeof data.thinking === "string",
  onGroupChatCatchup: (data) => data.content === undefined || typeof data.speaker === "string",
  onAgentStreamCatchup: (data) => data.content === undefined || typeof data.agent === "string",
  onCommandResult: isValidCommandResultPayload,
  onNoteCards: (data) => Array.isArray(data.notes)
    && isWsOptionalString(data, "header")
    && isWsOptionalString(data, "expression"),
  onMocUpdate: (data) => isWsRecord(data.data),
  onHeartbeatMiss: (data) => Array.isArray(data.misses),
  onRoomMessage: (data) => typeof data.sessionId === "string"
    && typeof data.sender === "string"
    && typeof data.text === "string"
    && (data.senderName === undefined || data.senderName === null || typeof data.senderName === "string")
    && (data.senderAvatar === undefined || data.senderAvatar === null || typeof data.senderAvatar === "string")
    && (data.ts === undefined || typeof data.ts === "string" || typeof data.ts === "number")
    && (data.attachments === undefined || isWsStrictAttachments(data.attachments)),
  onRoomMessageAck: (data) => typeof data.sessionId === "string"
    && typeof data.clientMessageId === "string"
    && isWsOptionalFiniteNumber(data, "logSeq")
    && isWsOptionalReplyQuote(data.replyTo),
  onArtifactCreated: (data) => typeof data.sessionId === "string" && data.artifact !== undefined,
  // Phase 4(bigtionax-chat-item-transfer): onArtifactCreated validator 형태 미러.
  onItemTransferred: (data) => typeof data.sessionId === "string" && data.artifact !== undefined,
  onMessageReadUpdate: (data) => Array.isArray(data.counts)
    && data.counts.every((item) => isWsRecord(item)
      && Number.isInteger(item.logSeq)
      && typeof item.count === "number"
      && Number.isFinite(item.count)
      && item.count >= 0),
  onRoomTyping: (data) => typeof data.isTyping === "boolean",
  // Phase 16(bigtionax-chat-reply-reactions-read-receipts): 개인 값이므로 0 이상의 유한수만 허용.
  onMessageUnreadTotal: (data) => typeof data.unreadCount === "number"
    && Number.isFinite(data.unreadCount)
    && data.unreadCount >= 0,
  // plaud-ops-log-hub T3: status 는 GET /api/meetings/sync/status 본문에서 ok 를 뺀 모양이다.
  onMeetingsSync: (data) => isWsRecord(data.status) && typeof data.status.running === "boolean",
  onYoutubeDigestJobUpdate: (data) => isWsRecord(data.job)
    && typeof data.job.id === "string"
    && typeof data.job.sessionId === "string"
    && typeof data.job.status === "string"
    && (data.job.roomId === null || data.job.roomId === undefined || typeof data.job.roomId === "string")
    && (data.job.sourceUrl === undefined || typeof data.job.sourceUrl === "string")
    && (data.job.videoId === undefined || typeof data.job.videoId === "string")
    && (data.job.error === null || data.job.error === undefined || typeof data.job.error === "string")
    && (data.job.notePath === null || data.job.notePath === undefined || typeof data.job.notePath === "string")
    && (data.job.artifactId === null || data.job.artifactId === undefined || typeof data.job.artifactId === "string")
    && (data.job.cancelRequestedAt === null || data.job.cancelRequestedAt === undefined || typeof data.job.cancelRequestedAt === "string")
    && (data.job.warningLabels === undefined || isWsStringArray(data.job.warningLabels)),
};

function isValidWsHandlerPayload(handlerName: string, data: Record<string, unknown>): boolean {
  if (wsRequiredStringFields[handlerName]?.some((key) => typeof data[key] !== "string")) return false;
  if (wsOptionalStringFields[handlerName]?.some((key) => !isWsOptionalString(data, key))) return false;
  if (wsOptionalBooleanFields[handlerName]?.some((key) => !isWsOptionalBoolean(data, key))) return false;
  return wsHandlerPayloadValidators[handlerName]?.(data) ?? true;
}
function guardWsHandlers<T extends object>(handlers: T): T {
  return new Proxy(handlers, {
    get(target, property, receiver) {
      const value: unknown = Reflect.get(target, property, receiver);
      if (typeof value !== "function" || property === "onReconnecting") return value;
      return (...args: unknown[]) => {
        if (args.length > 0 && (!isWsRecord(args[0]) || !isValidWsHandlerPayload(String(property), args[0]))) {
          console.warn(`[ws] ignored malformed payload for ${String(property)}`);
          return undefined;
        }
        return Reflect.apply(value, target, args);
      };
    },
  });
}
